import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, ShieldCheck, Star } from "lucide-react";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export const Hero = ({ onOpenChat }) => {
  const { t } = useLang();
  const stats = [
    [t.hero.stat1n, t.hero.stat1], [t.hero.stat2n, t.hero.stat2], [t.hero.stat3n, t.hero.stat3],
  ];
  return (
    <section id="top" className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden" data-testid="hero">
      <div className="absolute inset-0 z-0">
        <img src="https://images.unsplash.com/photo-1758322680193-56fe2af8fd59?crop=entropy&cs=srgb&fm=jpg&q=90&w=1920"
          alt="Voiture de société financée par I-LEASE en leasing en Belgique" className="w-full h-full object-cover scale-105" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1A2F] via-[#0A1A2F]/70 to-[#0A1A2F]/15" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1A2F] via-transparent to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 pl-2 pr-4 py-1.5 mb-7" data-testid="hero-badge">
          <span className="flex items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-[11px] font-bold text-white">
            <Star className="w-3 h-3 fill-white" strokeWidth={0} /> 2015
          </span>
          <span className="text-sm text-accent-foreground/90">{t.hero.badge}</span>
        </motion.div>

        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
          className="font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold max-w-4xl leading-[1.02]">
          {t.hero.title1}<br /><span className="text-accent">{t.hero.title2}</span>
        </motion.h1>

        <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
          {t.hero.subtitle}
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-9 flex flex-wrap gap-3">
          <Button onClick={() => scrollTo("quote")} data-testid="hero-cta-quote"
            className="rounded-full bg-white text-black hover:bg-accent hover:text-white transition-colors h-12 px-7 text-base font-medium group">
            {t.hero.cta1} <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
          </Button>
          <Button onClick={onOpenChat} data-testid="hero-cta-chat" variant="outline"
            className="rounded-full border-white/20 bg-white/5 hover:bg-white/10 h-12 px-7 text-base font-medium">
            <Sparkles className="mr-2 w-4 h-4 text-accent" strokeWidth={1.5} /> {t.hero.cta2}
          </Button>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-16 flex flex-wrap gap-8 sm:gap-14">
          {stats.map(([n, l], i) => (
            <div key={i} data-testid={`hero-stat-${i}`}>
              <p className="font-display text-3xl sm:text-4xl font-bold text-white">{n}</p>
              <p className="text-sm text-muted-foreground mt-1">{l}</p>
            </div>
          ))}
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-white/5 bg-black/40 backdrop-blur-sm py-2.5">
        <p className="text-center text-[11px] uppercase tracking-[0.2em] text-amber-300/60">{t.hero.warning}</p>
      </div>
    </section>
  );
};
